import {
  collection, doc, query, where, orderBy, documentId, startAfter, limit,
  onSnapshot, runTransaction, serverTimestamp
} from 'firebase/firestore';

export const PAGE_SIZE = 20;
export const PROFESSIONAL_FIELDS = ['Medicine', 'Dentistry', 'Nursing', 'Pharmacy', 'Allied Health', 'Other'];
export const STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
export const EDIT_FIELDS = ['displayName', 'professionalField', 'specialty', 'state', 'bio', 'topics', 'yearsExperience'];
export const DIRECTORY_FIELDS = [...EDIT_FIELDS, 'nameKey', 'published', 'createdAt', 'updatedAt'];
export const blankListing = { displayName: '', professionalField: '', specialty: '', state: '', bio: '', topics: '', yearsExperience: '' };
export const listingDraft = listing => Object.fromEntries(EDIT_FIELDS.map(key => [key, listing?.[key] ?? blankListing[key]]));
export const isMentor = role => String(role || '').toLowerCase() === 'mentor';
export function validateListing(draft) {
  const data = listingDraft(draft);
  for (const key of EDIT_FIELDS.filter(key => key !== 'yearsExperience')) {
    if (typeof data[key] !== 'string') throw new Error('Listing text must be valid text.');
    data[key] = data[key].trim();
  }
  for (const [key, max, required] of [['displayName', 80, true], ['specialty', 100, false], ['bio', 1200, true], ['topics', 300, false]]) {
    if ((required && !data[key]) || data[key].length > max) throw new Error(`Check ${key === 'displayName' ? 'display name' : key}: ${required ? 'required, ' : ''}maximum ${max} characters.`);
  }
  if (!PROFESSIONAL_FIELDS.includes(data.professionalField)) throw new Error('Select a professional field.');
  if (!STATES.includes(data.state)) throw new Error('Select a state.');
  if (data.yearsExperience !== null && !['number', 'string'].includes(typeof data.yearsExperience)) throw new Error('Years of experience must be a number or blank.');
  const years = data.yearsExperience === '' || data.yearsExperience === null ? null : Number(data.yearsExperience);
  if (years !== null && (!Number.isInteger(years) || years < 0 || years > 80)) throw new Error('Years of experience must be a whole number from 0 to 80, or blank.');
  return { ...data, yearsExperience: years };
}
export function directoryError(error) {
  if (error?.code === 'permission-denied') return 'Directory access was denied. Check your signed-in account and the directory security rules. Your session is still signed in.';
  if (error?.code === 'failed-precondition') return 'The directory query could not run. A required Firebase index may still need to be created or finish building.';
  if (error?.code === 'auth/session-changed') return 'Your signed-in account changed. Reload before continuing.';
  if (error?.code === 'unavailable' || error?.code === 'deadline-exceeded') return 'Could not confirm directory data with Firebase. Check your connection and retry.';
  return 'The directory request failed. Retry without signing out.';
}
export function directoryQuery(firestore, filters = {}, cursor = null) {
  const constraints = [where('published', '==', true)];
  if (filters.state) constraints.push(where('state', '==', filters.state));
  if (filters.professionalField) constraints.push(where('professionalField', '==', filters.professionalField));
  const name = String(filters.name || '').trim().toLowerCase();
  if (name) constraints.push(where('nameKey', '>=', name), where('nameKey', '<=', name + '\uf8ff'));
  constraints.push(orderBy('nameKey'), orderBy(documentId()));
  if (cursor) constraints.push(startAfter(cursor));
  return query(collection(firestore, 'mentorDirectory'), ...constraints, limit(PAGE_SIZE));
}

// Never use cached absence/pending local writes as proof of publication or withdrawal.
function observe(ref, next, error) {
  let closed = false;
  let timer;
  const waiting = () => { if (!timer) timer = setTimeout(() => { timer = null; if (!closed) error({ code: 'unavailable' }); }, 12000); };
  waiting();
  let stop;
  try { stop = onSnapshot(ref, { includeMetadataChanges: true }, snapshot => {
    if (closed) return;
    if (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites) { waiting(); return; }
    clearTimeout(timer); timer = null;
    next(snapshot);
  }, failure => { clearTimeout(timer); if (!closed) error(failure); }); }
  catch (failure) { clearTimeout(timer); closed = true; throw failure; }
  return () => { closed = true; clearTimeout(timer); stop(); };
}
export function createDirectoryService({ auth, firestore, account }) {
  const assertSession = () => {
    if (!account || account.isAnonymous || auth.currentUser?.uid !== account.uid) throw Object.assign(new Error('Session changed'), { code: 'auth/session-changed' });
  };
  return {
    subscribePage(filters, cursor, next, error) {
      assertSession();
      return observe(directoryQuery(firestore, filters, cursor), snapshot => next({
        rows: snapshot.docs.map(item => ({ ...item.data(), id: item.id })),
        cursor: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.size === PAGE_SIZE
      }), error);
    },
    subscribeListing(uid, next, error) {
      assertSession();
      return observe(doc(firestore, 'mentorDirectory', uid), snapshot => next(snapshot.exists() ? { ...snapshot.data(), id: snapshot.id } : null), error);
    },
    async publish(draft) {
      assertSession();
      const data = validateListing(draft);
      const ref = doc(firestore, 'mentorDirectory', account.uid);
      await runTransaction(firestore, async transaction => {
        assertSession();
        const existing = await transaction.get(ref);
        assertSession();
        transaction.set(ref, { ...data, nameKey: data.displayName.toLowerCase(), published: true,
          createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp(), updatedAt: serverTimestamp() });
      });
      assertSession();
    },
    async withdraw() {
      assertSession();
      const ref = doc(firestore, 'mentorDirectory', account.uid);
      await runTransaction(firestore, async transaction => {
        await transaction.get(ref);
        assertSession();
        transaction.delete(ref);
      });
      assertSession();
    }
  };
}
